/**
 * useFeedParser Hook
 * Upravljanje parsiranjem XML feeda
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { parseXmlStructure, parseXlsStructure, parseJsonStructure, isJsonContent, ParsedFeed } from '../../../lib/xmlParser';
import { autoDetectMappings } from '../utils/helpers';
import { SoapConfig } from '../types';

export interface UseFeedParserReturn {
  // State
  parsedFeed: ParsedFeed | null;
  xmlContent: string;
  loading: boolean;

  // Last loaded file info
  loadedFileName?: string;

  // Tree view state
  expanded: Set<string>;
  toggleExpand: (path: string) => void;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Sample navigation
  sampleIndex: number;
  setSampleIndex: (index: number) => void;

  // Actions
  fetchFeed: (
    feedUrl: string,
    authType: 'none' | 'url_params' | 'basic',
    authUsername: string,
    authPassword: string,
    existingMappings?: Record<string, string>,
    feedType?: 'xml' | 'soap' | 'excel',
    soapConfig?: SoapConfig,
    extraHeaders?: Record<string, string>
  ) => Promise<Record<string, string> | null>;

  loadFile: (file: File, existingMappings?: Record<string, string>) => Promise<Record<string, string> | null>;
  clearFeed: () => void;
  setParsedFeed: (feed: ParsedFeed | null) => void;
  setXmlContent: (content: string) => void;
}

export function useFeedParser(): UseFeedParserReturn {
  const [parsedFeed, setParsedFeed] = useState<ParsedFeed | null>(null);
  const [xmlContent, setXmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadedFileName, setLoadedFileName] = useState<string | undefined>(undefined);

  // Tree view
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Sample navigation
  const [sampleIndex, setSampleIndex] = useState(0);

  /**
   * Toggle expand/collapse u tree view-u
   */
  const toggleExpand = useCallback((path: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  /**
   * Load and parse feed from local file (XML/XLS/XLSX)
   */
  const loadFile = useCallback(async (file: File, existingMappings?: Record<string, string>): Promise<Record<string, string> | null> => {
    setLoading(true);
    setParsedFeed(null);

    try {
      const fileName = file.name || 'upload';
      const lowerName = fileName.toLowerCase();
      setLoadedFileName(fileName);

      if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        const parsed = parseXlsStructure(buffer);

        // Store placeholder content (import path expects xmlContent string)
        setXmlContent('');
        setParsedFeed(parsed);
        setExpanded(new Set([parsed.rootElement]));
        setSampleIndex(0);
        toast.success(`XLS učitan: ${parsed.totalRecords} redova pronađeno`);

        if (!existingMappings || Object.keys(existingMappings).length === 0) {
          return autoDetectMappings(parsed.fields);
        }
        return existingMappings;
      }

      // Default: treat as text/XML
      const content = await file.text();
      setXmlContent(content);
      const parsed = parseXmlStructure(content);
      setParsedFeed(parsed);
      setExpanded(new Set([parsed.rootElement]));
      setSampleIndex(0);
      toast.success(`Feed učitan: ${parsed.totalRecords} zapisa pronađeno`);

      if (!existingMappings || Object.keys(existingMappings).length === 0) {
        return autoDetectMappings(parsed.fields);
      }
      return existingMappings;
    } catch (error: any) {
      console.error('File load error:', error);
      toast.error('Greška: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Dohvati i parsiraj feed (XML ili SOAP)
   */
  const fetchFeed = useCallback(async (
    feedUrl: string,
    authType: 'none' | 'url_params' | 'basic',
    authUsername: string,
    authPassword: string,
    existingMappings?: Record<string, string>,
    feedType: 'xml' | 'soap' | 'excel' = 'xml',
    soapConfig?: SoapConfig,
    extraHeaders?: Record<string, string>
  ): Promise<Record<string, string> | null> => {
    // Validate based on feed type
    if ((feedType === 'xml' || feedType === 'excel') && !feedUrl) {
      toast.error('Unesi URL feeda');
      return null;
    }
    if (feedType === 'soap' && (!soapConfig?.endpoint_url || !soapConfig?.soap_action || !soapConfig?.method || !soapConfig?.namespace)) {
      toast.error('Popuni sve SOAP parametre');
      return null;
    }

    setLoading(true);
    setParsedFeed(null);

    try {
      let result: { success: boolean; content?: string; error?: string };

      // =====================================================================
      // EXCEL URL FETCH (client-side)
      // =====================================================================
      if (feedType === 'excel') {
        let url = feedUrl;
        if (authType === 'url_params' && authUsername && authPassword) {
          const urlObj = new URL(feedUrl);
          urlObj.searchParams.set('USERNAME', authUsername);
          urlObj.searchParams.set('PASSWORD', authPassword);
          url = urlObj.toString();
        }

        let buffer: ArrayBuffer | null = null;
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch Excel: ${response.status} ${response.statusText}`);
          }
          buffer = await response.arrayBuffer();
        } catch (err: any) {
          // Common in browsers if remote server blocks CORS.
          // Try server-side proxy fallback (if endpoint exists).
          try {
            const proxyUrl = `/api/feed/excel-proxy?url=${encodeURIComponent(url)}`;
            const proxyResponse = await fetch(proxyUrl);
            if (!proxyResponse.ok) {
              throw new Error(`Excel proxy failed: ${proxyResponse.status} ${proxyResponse.statusText}`);
            }
            buffer = await proxyResponse.arrayBuffer();
          } catch {
            throw new Error('Ne mogu učitati Excel sa URL-a (CORS ili mrežna greška). Probaj upload fajla ili dodaj proxy endpoint.');
          }
        }

        if (!buffer) {
          throw new Error('Excel buffer is empty');
        }

        const parsed = parseXlsStructure(buffer);

        setXmlContent('');
        setParsedFeed(parsed);
        setExpanded(new Set([parsed.rootElement]));
        setSampleIndex(0);
        toast.success(`Excel učitan: ${parsed.totalRecords} redova pronađeno`);

        if (!existingMappings || Object.keys(existingMappings).length === 0) {
          return autoDetectMappings(parsed.fields);
        }
        return existingMappings;
      }

      if (feedType === 'soap' && soapConfig) {
        // SOAP request
        console.log('[FeedParser] Making SOAP request:', soapConfig);
        const response = await fetch('/api/feed/soap', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(soapConfig),
        });
        result = await response.json();
      } else {
        // Regular XML/JSON fetch
        let fetchUrlParams = `url=${encodeURIComponent(feedUrl)}`;
        if (authType !== 'none' && authUsername && authPassword) {
          fetchUrlParams += `&username=${encodeURIComponent(authUsername)}&password=${encodeURIComponent(authPassword)}`;
          fetchUrlParams += `&auth_type=${encodeURIComponent(authType)}`;
        }
        if (extraHeaders && Object.keys(extraHeaders).length > 0) {
          fetchUrlParams += `&extra_headers=${encodeURIComponent(JSON.stringify(extraHeaders))}`;
        }
        const response = await fetch(`/api/feed/fetch?${fetchUrlParams}`);
        result = await response.json();
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch feed');
      }

      const content = result.content || '';
      setXmlContent(content);

      // Auto-detect JSON vs XML and parse accordingly
      const parsed = isJsonContent(content)
        ? parseJsonStructure(content)
        : parseXmlStructure(content);
      console.log('[FeedParser] Parsed feed:', {
        rootElement: parsed.rootElement,
        recordElement: parsed.recordElement,
        totalRecords: parsed.totalRecords,
        fieldsCount: parsed.fields.length,
        fields: parsed.fields.slice(0, 10).map(f => f.displayPath),
      });

      setParsedFeed(parsed);

      // Expand root
      setExpanded(new Set([parsed.rootElement]));

      // Reset sample index
      setSampleIndex(0);

      toast.success(`Feed učitan: ${parsed.totalRecords} zapisa pronađeno`);

      // Auto-detect mappings if no existing
      if (!existingMappings || Object.keys(existingMappings).length === 0) {
        return autoDetectMappings(parsed.fields);
      }

      return existingMappings;
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error('Greška: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Očisti parsirani feed
   */
  const clearFeed = useCallback(() => {
    setParsedFeed(null);
    setXmlContent('');
    setLoadedFileName(undefined);
    setExpanded(new Set());
    setSampleIndex(0);
  }, []);

  return {
    // State
    parsedFeed,
    xmlContent,
    loading,
    loadedFileName,

    // Tree view
    expanded,
    toggleExpand,
    setExpanded,

    // Sample navigation
    sampleIndex,
    setSampleIndex,

    // Actions
    fetchFeed,
    loadFile,
    clearFeed,
    setParsedFeed,
    setXmlContent,
  };
}
